import {IDataService} from "@/shared/DataService/IDataService";
import {IPlexSummary} from "@/shared/DataService/IPlexSummary";
import {Plex} from "@/shared/DataService/plex";
import * as _ from "lodash";
import {IEdgeModel} from "@/shared/models/abstract/IEdgeModel";
import {INodeModel} from "@/shared/models/abstract/INodeModel";
import LocalDataStore from "@/shared/DataService/localDataStore";
import {IEntitySummary} from "@/shared/DataService/IEntitySummary";
import ModelFactory from "@/shared/models/modelFactory";
import {EntityType} from "@/shared/models/entityType";
import {DefaultEdgeModel} from "@/shared/models/defaultEdge";

export default class LocalDataService implements IDataService {
    private $store: any;
    private localStore: LocalDataStore;
    private readonly edges: IEdgeModel[];
    private readonly nodes: INodeModel[];

    constructor($store) {
        this.$store = $store;
        this.localStore = new LocalDataStore();
        this.edges = this.$store.state.plex.edges;
        this.nodes = this.$store.state.plex.nodes;
    }

    /** @inheritdoc */
    async nameExists(name: string): Promise<IEntitySummary> {
        const found = this.nodes.find((n: INodeModel) => {
            return n.name.toLowerCase().trim().indexOf((name || "").toLowerCase()) > -1;
        })
        if (_.isNil(found)) {
            return null;
        } else {
            return {
                text: found.name,
                value: found.id,
                entityType: found.entityType
            }
        }

    }

    /** @inheritdoc */
    async searchName(v: string, includeGroups: boolean): Promise<IEntitySummary[]> {
        const withGroups = (e: INodeModel) => (e.name || "").toLowerCase().indexOf((v || "").toLowerCase()) > -1;
        const withoutGroups = (e: INodeModel) => (!e.isGroup) && (e.name || "").toLowerCase().indexOf((v || "").toLowerCase()) > -1;
        return this.nodes.filter(e => {
            return includeGroups ? withGroups(e) : withoutGroups(e);
        }).map(n => {
            return {
                text: n.name,
                value: n.id,
                entityType: n.entityType
            }
        });
    }

    /** @inheritdoc */
    getAllPlexes(): Promise<IPlexSummary[]> {
        return this.localStore.getAllPlexes();
    }

    /** @inheritdoc */
    getPlex(id: string): Promise<Plex> {
        return this.localStore.getPlex(id);
    }

    /** @inheritdoc */
    plexCount(): Promise<number> {
        return this.localStore.plexCount();
    }

    /** @inheritdoc */
    plexIdExists(id: string): Promise<boolean> {
        return this.localStore.plexIdExists(id);
    }

    /** @inheritdoc */
    removePlex(id: string): Promise<boolean> {
        return this.localStore.removePlex(id);
    }

    /** @inheritdoc */
    upsertPlex(plex: Plex): Promise<boolean> {
        return this.localStore.upsertPlex(plex);
    }

    /** @inheritdoc */
    async getNodeById(id: string): Promise<INodeModel> {
        return this.nodes.find(n => n.id === id);
    }

    /** @inheritdoc */
    async getEdgeById(id: string): Promise<IEdgeModel> {
        return this.edges.find((e: IEdgeModel) => e.id === id);
    }

    /** @inheritdoc */
    async getParents(id: string): Promise<INodeModel[]> {
        const coll = [];
        for (const e of this.edges) {
            if (e.targetId === id) {
                const sourceNode = await this.getNodeById(e.sourceId);
                if (!_.isNil(sourceNode)) {
                    // need to clone in case we have multiplicity.
                    // this is really because the whole plex sits in the redux store and pointers can point to the same object
                    const clone = sourceNode.clone();
                    clone["edgeId"] = e.id;
                    // but to preserve the link we need the same id
                    clone["id"] = sourceNode.id;
                    clone["edgeName"] = e.name;
                    coll.push(clone);
                }
            }
        }
        return coll;
    }

    /** @inheritdoc */
    async getChildren(id: string): Promise<INodeModel[]> {
        const coll = [];
        for (let i = 0; i < this.edges.length; i++) {
            const e = this.edges[i];
            if (e.sourceId === id) {
                const targetNode = await this.getNodeById(e.targetId);
                if (!_.isNil(targetNode)) {
                    // need to clone in case we have multiplicity.
                    // this is really because the whole plex sits in the redux store and pointers can point to the same object
                    const clone = targetNode.clone();
                    clone["edgeId"] = e.id;
                    // but to preserve the link we need the same id
                    clone["id"] = targetNode.id;
                    clone["edgeName"] = e.name;
                    coll.push(clone);
                }
            }
        }
        return coll;
    }

    /** @inheritdoc */
    async upsertNode(model: INodeModel): Promise<boolean> {
        return this.saveNode(model);
    }

    /** @inheritdoc */
    async upsertEdge(model: IEdgeModel): Promise<boolean> {
        return this.saveEdge(model);
    }

    async removeGroup(model: INodeModel): Promise<boolean> {
        if (_.isNil(model)) {
            console.error("Attempt to delete non-existing group.");
            return;
        }
        if (!model.isGroup) {
            throw new Error("Use the 'removeNode' method for normal nodes.");
        }
        const id = model.id;
        this.nodes.forEach((n: INodeModel) => {
            if (n.groupId === id) {
                n.groupId = null;
            }
        });
        _.remove(this.nodes, (n: INodeModel) => n.id === id);
        return this.save();
    }

    /** @inheritdoc */
    async removeNode(model: INodeModel): Promise<boolean> {
        if (_.isNil(model)) {
            console.error("Attempt to delete non-existing node.");
            return;
        }
        if (model.isGroup) {
            throw new Error("Use the 'removeGroup' method for groups.");
        }
        return this.removeNodeById(model.id);
    }

    /** @inheritdoc */
    async removeNodeById(id: string): Promise<boolean> {
        const model = await this.getNodeById(id);
        if (_.isNil(model)) {
            console.error("Attempt to delete non-existing node.");
            return;
        }
        if (model.isGroup) {
            throw new Error("Use the 'removeGroup' method for groups.");
        }
        // delete the edges to or from the node being deleted
        _.remove(this.edges, (e: IEdgeModel) => {
            return e.sourceId === id || e.targetId === id;
        });
        _.remove(this.nodes, (n: INodeModel) => n.id === id);
        return this.save();
    }

    /** @inheritdoc */
    async removeEdgeById(id: string): Promise<boolean> {
        _.remove(this.edges, (n: IEdgeModel) => n.id === id);
        return this.save();
    }

    /** @inheritdoc */
    async removeEdge(model: IEdgeModel): Promise<boolean> {
        _.remove(this.edges, (e: IEdgeModel) => e.id === model.id);
        return this.save();
    }

    /** @inheritdoc */
    private async saveNode(node: INodeModel) {
        // remove if already present
        _.remove(this.nodes, (n: INodeModel) => n.id === node.id);
        node.dateChanged = (new Date()).getTime();
        this.nodes.push(node);
        return this.save();
    }

    /** @inheritdoc */
    private async saveEdge(edge: IEdgeModel) {
        // remove if already present
        _.remove(this.edges, (n: IEdgeModel) => n.id === edge.id);
        edge.dateChanged = (new Date()).getTime();
        this.edges.push(edge);
        return this.save();
    }

    /** @inheritdoc */
    getFavorites(count: number = 10): INodeModel[] {
        count = Math.min(100, Math.max(0, count));
        return _.take(this.nodes.filter(n => n.isFavorite), count);
    }

    private async save() {
        return this.localStore.upsertPlex(this.$store.state.plex);
    }

    /** @inheritdoc */
    async getGroupHierarchy(id: string): Promise<INodeModel[]> {
        const model = await this.getNodeById(id);
        if (_.isNil(model)) {
            console.error("Attempt to get the group hierarchy for nil.");
            return [];
        }
        const coll = [];
        let parentId = (model as INodeModel).groupId;
        while (!_.isNil(parentId)) {
            const item = await this.getNodeById(parentId);
            if (_.isNil(item)) {
                break;
            }
            coll.push(item);
            parentId = item.groupId;
        }
        return coll.reverse();
    }

    /** @inheritdoc */
    async searchGroups(v: string): Promise<IEntitySummary[]> {
        return this.nodes.filter((n: INodeModel) => {
            return (n.isGroup) && (n.name || "").toLowerCase().indexOf((v || "").toLowerCase()) > -1
        }).map(n => {
            return {
                text: n.name,
                value: n.id,
                entityType: n.entityType
            }
        });
    }

    /** @inheritdoc */
    async getGroupChildren(id: string): Promise<IEntitySummary[]> {
        return this.nodes.filter((n: INodeModel) => {
            return (n.groupId === id)
        }).map(n => {
            return {
                text: n.name,
                value: n.id,
                entityType: n.entityType
            }
        });
    }

    /** @inheritdoc */
    async setParentGroup(nodeId: string, groupId: string): Promise<boolean> {
        const node = await this.getNodeById(nodeId);
        if (_.isNil(node)) {
            throw new Error(`Node with id '${nodeId}' not found.`);
        }
        if (_.isNil(groupId)) {
            node.groupId = null;
            return this.saveNode(node);
        } else {
            const group = await this.getNodeById(groupId);
            if (_.isNil(group)) {
                throw new Error(`Node with id '${groupId}' not found.`);
            }
            if (!group.isGroup) {
                throw new Error(`Given group id '${groupId}' is not a group.`);
            }
            node.groupId = groupId;
            return this.saveNode(node);
        }
    }

    /** @inheritdoc */
    async connect(sourceId: string, targetId: string, name: string): Promise<string> {
        const source = await this.getNodeById(sourceId);
        const target = await this.getNodeById(targetId);
        if (_.isNil(source)) {
            throw new Error(`The node with id '${sourceId}' does not exist.`)
        }
        if (_.isNil(targetId)) {
            throw new Error(`The node with id '${targetId}' does not exist.`)
        }
        if (source.isGroup) {
            throw new Error(`The node '${sourceId}' is a group and can't be the source of an edge.`)
        }
        if (target.isGroup) {
            throw new Error(`The node '${targetId}' is a group and can't be the target of an edge.`)
        }
        const model = ModelFactory.createNew(EntityType.DefaultEdge) as DefaultEdgeModel;
        model.sourceId = sourceId;
        model.targetId = targetId;
        model.name = name;
        await this.upsertEdge(model);
        return model.id;
    }

    async areConnected(sourceId: string, targetId: string, anyDirection: boolean = true): Promise<boolean> {
        for (let i = 0; i < this.edges.length; i++) {
            const e = this.edges[i];
            if (e.sourceId === sourceId && e.targetId === targetId) {
                return true;
            }
            if (anyDirection) {
                if (e.sourceId === targetId && e.targetId === sourceId) {
                    return true;
                }
            }
        }
        return false;
    }

    async edgeExists(id: string): Promise<boolean> {
        const found = await this.getEdgeById(id);
        return !_.isNil(found);
    }

    async getEdgesBetween(sourceId: string, targetId: string, anyDirection: boolean): Promise<IEdgeModel[]> {
        const coll = [];
        this.edges.forEach((e: IEdgeModel) => {
            if (e.sourceId === sourceId && e.targetId === targetId) {
                coll.push(e);
                return; // can't have both direction
            }
            if (anyDirection) {
                if (e.sourceId === targetId && e.targetId === sourceId) {
                    coll.push(e)
                }
            }
        });
        return coll;
    }

    async groupExists(id: string): Promise<boolean> {
        const found = await this.getNodeById(id);
        return !_.isNil(found) && found.isGroup;
    }

    async nodeExists(id: string): Promise<boolean> {
        const found = await this.getNodeById(id);
        return !_.isNil(found);
    }

    /** @inheritdoc */
    async getItemsOfType(entityType: EntityType, count: number = 10) {
        count = Math.min(100, Math.max(0, count));
        if (count === 0) {
            return []
        }
        if (entityType === EntityType.DefaultEdge) {
            return _.take(this.edges, count);
        } else {
            return _.take(this.nodes.filter(n => n.entityType === entityType), count);
        }
    }
}
